import React, { useEffect, useMemo, useState } from 'react';
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Plugin,
  Tooltip,
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import ArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import ArrowRight from '@mui/icons-material/KeyboardArrowRight';
import * as S from './Reporting.styled';
import {
  formatReportMetricTotal,
  ReportMetricChart,
  ReportMetricsSummary,
  reportMetricColors,
  reportPieFigureColor,
  truncatePieSliceLabel,
} from './reportMetrics';

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Filler,
  Tooltip,
  Legend,
);

/**
 * Draw total in the doughnut chartArea center so it stays aligned when the
 * legend grows (count pies with many types).
 */
const makePieCenterPlugin = (
  total: number,
  label: string | undefined,
  asMoney: boolean | undefined,
): Plugin<'doughnut'> => ({
  id: 'reportPieCenterOverlay',
  // After slices, before tooltip — so hover tooltips paint on top of the center total.
  afterDatasetsDraw(chart) {
    // Only doughnut configs have a hole; skip if chartArea isn't ready.
    const { ctx } = chart;
    const { left, right, top, bottom } = chart.chartArea;
    if (right <= left || bottom <= top) return;
    const x = (left + right) / 2;
    const y = (top + bottom) / 2;
    const valueText = formatReportMetricTotal(total, asMoney);
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    // Signed amount: green gain / red loss vs blue pie slices.
    ctx.fillStyle = reportPieFigureColor(total);
    ctx.font = 'bold 15px sans-serif';
    if (label) {
      ctx.fillText(valueText, x, y - 7);
      ctx.fillStyle = '#3d4450';
      ctx.font = '600 10px sans-serif';
      ctx.fillText(String(label).toUpperCase(), x, y + 10);
    } else {
      ctx.fillText(valueText, x, y);
    }
    ctx.restore();
  },
});

const ReportMetricsCarousel = ({
  metrics,
  compact,
  setLabel,
}: {
  metrics: ReportMetricsSummary,
  /** Tighter height for side preview / receiver panel. */
  compact?: boolean,
  /** Dark centered set name (ActivityLogs 1) — chart title sits under it. */
  setLabel?: string,
}) => {
  const charts = metrics.charts;
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setIndex(0);
  }, [charts.length, charts[0]?.id]);

  const safeIndex = charts.length ? Math.min(index, charts.length - 1) : 0;
  const chart: ReportMetricChart | null = charts[safeIndex] || null;

  const data = useMemo(() => {
    if (!chart) return null;
    const colors = reportMetricColors(chart.labels.length);
    if (chart.kind === 'line') {
      const stroke = '#2563eb';
      return {
        labels: chart.labels,
        datasets: [{
          data: chart.values,
          borderColor: stroke,
          backgroundColor: 'rgba(37, 99, 235, 0.12)',
          pointBackgroundColor: colors,
          pointBorderColor: '#ffffff',
          pointRadius: 4,
          pointHoverRadius: 5,
          borderWidth: 2,
          fill: true,
          tension: 0.25,
        }],
      };
    }
    return {
      labels: chart.labels,
      datasets: [{
        data: chart.values,
        backgroundColor: colors,
        borderColor: '#ffffff',
        borderWidth: chart.kind === 'doughnut' ? 2 : 0,
        borderRadius: chart.kind === 'bar' ? 4 : 0,
      }],
    };
  }, [chart]);

  const doughnutPlugins = useMemo(() => {
  // Center total is balance-only (money pies). Count pies keep a plain Total.
  if (
    !chart
    || chart.kind !== 'doughnut'
    || chart.centerTotal == null
  ) {
    return [];
  }
  return [makePieCenterPlugin(chart.centerTotal, chart.centerLabel, chart.formatAsMoney)];
  }, [chart]);

  if (!charts.length || !chart || !data) return null;

  const chartHeight = compact ? 160 : 200;
  const cartesianOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx: { raw?: unknown }) => {
            const v = Number(ctx.raw || 0);
            return chart.formatAsMoney
              ? ` ${v.toLocaleString()}`
              : ` ${v}`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: { font: { size: 10 }, maxRotation: 45, minRotation: 0 },
        grid: { display: false },
      },
      y: {
        ticks: { font: { size: 10 } },
        grid: { color: 'rgba(0,0,0,0.06)' },
      },
    },
  };

  return (
    <S.MetricsCarousel>
      <S.MetricsCarouselHeader>
        <S.MetricsNavBtn
          type="button"
          aria-label="Previous chart"
          disabled={charts.length < 2}
          onClick={() => setIndex((i) => (i - 1 + charts.length) % charts.length)}
        >
          <ArrowLeft fontSize="small" />
        </S.MetricsNavBtn>
        <S.MetricsCarouselTitle>
          {setLabel || chart.title}
          {setLabel ? (
            <S.MetricsCarouselChartName>{chart.title}</S.MetricsCarouselChartName>
          ) : null}
          <S.MetricsCarouselMeta>
            {safeIndex + 1}
            {' / '}
            {charts.length}
            {' · '}
            {metrics.headerCountKind === 'balances'
              ? metrics.valueLogCount
              : metrics.logCount}
            {' '}
            {metrics.headerCountKind === 'balances' ? 'balances' : 'logs'}
            {metrics.headerCountKind === 'balances'
              && metrics.logCount > metrics.valueLogCount
              ? ` · ${metrics.logCount - metrics.valueLogCount} logs`
              : ''}
          </S.MetricsCarouselMeta>
        </S.MetricsCarouselTitle>
        <S.MetricsNavBtn
          type="button"
          aria-label="Next chart"
          disabled={charts.length < 2}
          onClick={() => setIndex((i) => (i + 1) % charts.length)}
        >
          <ArrowRight fontSize="small" />
        </S.MetricsNavBtn>
      </S.MetricsCarouselHeader>
      <S.MetricsChartWrap $height={chartHeight}>
        {chart.kind === 'doughnut' ? (
          <Doughnut
            data={data}
            plugins={doughnutPlugins}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              cutout: '58%',
              plugins: {
                legend: {
                  position: 'bottom',
                  labels: {
                    boxWidth: 10,
                    font: { size: 11 },
                    // Short legend text only — tooltips keep the full slice name.
                    generateLabels: (c) => {
                      const raw = c.data.labels || [];
                      const ds = c.data.datasets[0];
                      const bgColors = Array.isArray(ds?.backgroundColor)
                        ? ds.backgroundColor
                        : [];
                      return raw.map((label, i) => ({
                        text: truncatePieSliceLabel(String(label ?? '')),
                        fillStyle: bgColors[i] || '#2563eb',
                        strokeStyle: '#ffffff',
                        lineWidth: 2,
                        hidden: !c.getDataVisibility(i),
                        index: i,
                      }));
                    },
                  },
                },
                tooltip: {
                  callbacks: {
                    label: (ctx) => {
                      const v = Number(ctx.raw || 0);
                      const name = String(ctx.label || '');
                      if (chart.formatAsMoney) {
                        return ` ${name}: ${v.toLocaleString()}`;
                      }
                      return ` ${name}: ${v}`;
                    },
                  },
                },
              },
            }}
          />
        ) : chart.kind === 'line' ? (
          <Line data={data} options={cartesianOptions} />
        ) : (
          <Bar data={data} options={cartesianOptions} />
        )}
      </S.MetricsChartWrap>
    </S.MetricsCarousel>
  );
};

export default ReportMetricsCarousel;
